<template>
  <div class="mt-4 px-5">
    <v-alert dense text shaped type="info">
      <b>Beitragsgruppen</b>
      <div>
        Verwalten Sie hier Ihre Beitragsgruppen und die zugehörigen Soll-Arbeitsstunden pro Jahr.
      </div>
    </v-alert>

    <v-row>
      <v-col cols="12" md="6">
        <v-data-table
          :headers="headers"
          :items="$store.state.ClubApp.Beitragsgruppen"
          :loading="loading"
          class="elevation-1"
          @click:row="selectGroup"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>Gruppen</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn color="primary" small @click="openCreateDialog">Neu</v-btn>
            </v-toolbar>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon small class="mr-2" @click.stop="editGroup(item)">mdi-pencil</v-icon>
            <v-icon small @click.stop="deleteGroup(item)">mdi-delete</v-icon>
          </template>
        </v-data-table>
      </v-col>

      <v-col cols="12" md="6">
        <v-card v-if="selectedGroup" class="elevation-1">
          <v-toolbar flat color="grey lighten-4">
            <v-toolbar-title>Mitglieder: {{ selectedGroup.name }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              v-if="selectedTableMembers && selectedTableMembers.length > 0"
              color="error"
              small
              class="mr-2"
              @click="bulkUnassignMembers"
            >
              <v-icon left small>mdi-delete</v-icon>
              Markierte entfernen ({{ selectedTableMembers.length }})
            </v-btn>
            <v-btn color="success" small @click="openAssignDialog">Zuweisen</v-btn>
          </v-toolbar>
          <v-data-table
            v-model="selectedTableMembers"
            :headers="memberHeaders"
            :items="$store.state.ClubApp.BeitragsgruppeMembers"
            :loading="membersLoading"
            show-select
            dense
          >
            <template v-slot:item.actions="{ item }">
              <v-btn x-small color="error" @click="unassignMember(item)">Entfernen</v-btn>
            </template>
          </v-data-table>
        </v-card>
        <v-alert v-else type="info" text>Wählen Sie eine Gruppe aus, um Mitglieder zu verwalten.</v-alert>
      </v-col>
    </v-row>

    <!-- Group Create/Edit Dialog -->
    <v-dialog v-model="groupDialog" max-width="500px">
      <v-card>
        <v-card-title>{{ editedIndex === -1 ? 'Neue Gruppe' : 'Gruppe bearbeiten' }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="editedGroup.name" label="Name"></v-text-field>
          <v-text-field v-model="editedGroup.soll_stunden_jahr" label="Soll-Stunden/Jahr" type="number"></v-text-field>
          <v-textarea v-model="editedGroup.beschreibung" label="Beschreibung" rows="2"></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="groupDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" @click="saveGroup">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Member Assign Dialog -->
    <v-dialog v-model="assignDialog" max-width="600px">
      <v-card>
        <v-card-title>Mitglieder zuweisen</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="selectedUsers"
            :items="allClubMembers"
            item-text="full_name"
            item-value="UserID"
            label="Mitglieder suchen"
            multiple
            chips
            deletable-chips
            small-chips
          ></v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="assignDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" @click="assignMembers" :disabled="!selectedUsers || selectedUsers.length === 0">Zuweisen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>

export default {
  name: "ContributionGroupsList",
  data() {
    return {
      loading: false,
      membersLoading: false,
      selectedGroup: null,
      allClubMembers: [],
      headers: [
        { text: "Name", value: "name" },
        { text: "Soll/Jahr", value: "soll_stunden_jahr" },
        { text: "Aktionen", value: "actions", sortable: false },
      ],
      memberHeaders: [
        { text: "Name", value: "name" },
        { text: "Vorname", value: "vorname" },
        { text: "Aktionen", value: "actions", sortable: false },
      ],
      groupDialog: false,
      editedIndex: -1,
      editedGroup: { name: "", soll_stunden_jahr: 0, beschreibung: "" },
      assignDialog: false,
      selectedUsers: [],
      selectedTableMembers: [],
    };
  },
  mounted() {
    this.fetchData();
    this.fetchAllMembers();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        await this.$store.dispatch("ClubApp/getBeitragsgruppen");
      } catch (error) {
        console.error("Fehler beim Laden der Beitragsgruppen:", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchAllMembers() {
      try {
        // Wir nutzen den bestehenden Store-Action falls möglich, oder direkten API Call
        const response = await this.$store.dispatch("ClubApp/getClubMember");
        // Der Action im Store committet bereits zu ClubMember, aber wir können die Daten auch vom Response nutzen
        // Falls die Action keinen return hat, holen wir es aus dem State
        const members = response && response.data ? response.data : this.$store.state.ClubApp.ClubMember;
        this.allClubMembers = members.map(m => ({
          ...m,
          full_name: `${m.name}, ${m.vorname} (${m.mitgliedsnummer || 'keine Nr.'})`
        }));
      } catch (error) {
        console.error("Fehler beim Laden der Vereinsmitglieder:", error);
      }
    },
    async selectGroup(group) {
      this.selectedGroup = group;
      this.membersLoading = true;
      try {
        await this.$store.dispatch("ClubApp/getBeitragsgruppeMembers", group.id);
      } catch (error) {
        console.error("Fehler beim Laden der Gruppenmitglieder:", error);
      } finally {
        this.membersLoading = false;
      }
    },
    openCreateDialog() {
      this.editedIndex = -1;
      this.editedGroup = { name: "", soll_stunden_jahr: 0, beschreibung: "" };
      this.groupDialog = true;
    },
    editGroup(item) {
      this.editedIndex = this.$store.state.ClubApp.Beitragsgruppen.indexOf(item);
      this.editedGroup = Object.assign({}, item);
      this.groupDialog = true;
    },
    async saveGroup() {
      try {
        if (this.editedIndex > -1) {
          await this.$store.dispatch("ClubApp/updateBeitragsgruppe", { id: this.editedGroup.id, data: this.editedGroup });
        } else {
          await this.$store.dispatch("ClubApp/createBeitragsgruppe", this.editedGroup);
        }
        this.groupDialog = false;
        this.fetchData();
      } catch (error) {
        console.error("Fehler beim Speichern der Gruppe:", error);
      }
    },
    async deleteGroup(item) {
      if (confirm("Beitragsgruppe wirklich löschen?")) {
        try {
          await this.$store.dispatch("ClubApp/deleteBeitragsgruppe", item.id);
          if (this.selectedGroup && this.selectedGroup.id === item.id) this.selectedGroup = null;
          this.fetchData();
        } catch (error) {
          console.error("Fehler beim Löschen der Gruppe:", error);
        }
      }
    },
    openAssignDialog() {
      this.selectedUsers = [];
      this.assignDialog = true;
    },
    async assignMembers() {
      try {
        await this.$store.dispatch("ClubApp/bulkAssignBeitragsgruppe", {
          user_ids: this.selectedUsers,
          beitragsgruppe_id: this.selectedGroup.id
        });
        this.assignDialog = false;
        this.selectGroup(this.selectedGroup);
      } catch (error) {
        console.error("Fehler bei der Zuweisung:", error);
      }
    },
    async unassignMember(member) {
      try {
        await this.$store.dispatch("ClubApp/bulkAssignBeitragsgruppe", {
          user_ids: [member.id],
          beitragsgruppe_id: null
        });
        this.selectGroup(this.selectedGroup);
      } catch (error) {
        console.error("Fehler beim Entfernen:", error);
      }
    },
    async bulkUnassignMembers() {
      if (!this.selectedTableMembers.length) return;
      if (!confirm(`Möchten Sie ${this.selectedTableMembers.length} Mitglieder wirklich aus dieser Gruppe entfernen?`)) return;

      try {
        await this.$store.dispatch("ClubApp/bulkAssignBeitragsgruppe", {
          user_ids: this.selectedTableMembers.map(m => m.id),
          beitragsgruppe_id: null
        });
        this.selectedTableMembers = [];
        this.selectGroup(this.selectedGroup);
      } catch (error) {
        console.error("Fehler beim Bulk-Entfernen:", error);
      }
    }
  }
};
</script>
